import React, { useState, useEffect } from 'react';
import { adminAPI } from '../../services/api';
import './AdminPushNotifications.css';

interface PushSubscription {
  id: number;
  user_id: number;
  endpoint: string;
  created_at: string;
  user_name: string;
  user_email: string;
}

interface PushStats {
  total: number;
  unique_users: number;
}

const AdminPushNotifications: React.FC = () => {
  const [subscriptions, setSubscriptions] = useState<PushSubscription[]>([]);
  const [stats, setStats] = useState<PushStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    loadSubscriptions();
  }, []);

  const loadSubscriptions = async () => {
    try {
      setIsLoading(true);
      const response = await adminAPI.getPushSubscriptions();
      setSubscriptions(response.data.data.subscriptions);
      setStats(response.data.data.stats);
    } catch (err: any) {
      setError('Error al cargar suscripciones push');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteSubscription = async (id: number) => {
    if (!window.confirm('Estas seguro de eliminar esta suscripcion?')) return;

    try {
      await adminAPI.deletePushSubscription(id);
      setSubscriptions(prev => prev.filter(s => s.id !== id));
      setSuccessMessage('Suscripcion eliminada');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err: any) {
      setError('Error al eliminar suscripcion');
      setTimeout(() => setError(''), 3000);
    }
  };

  const formatDate = (dateStr: string) => {
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('es-ES', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateStr;
    }
  };

  const getBrowserFromEndpoint = (endpoint: string) => {
    if (endpoint.includes('fcm.googleapis.com')) return { name: 'Chrome/Android', icon: '🌐' };
    if (endpoint.includes('mozilla.com')) return { name: 'Firefox', icon: '🦊' };
    if (endpoint.includes('apple.com') || endpoint.includes('push.apple')) return { name: 'Safari', icon: '🍎' };
    if (endpoint.includes('windows.com')) return { name: 'Edge', icon: '🪟' };
    return { name: 'Desconocido', icon: '📱' };
  };

  if (isLoading) {
    return <div className="admin-loading">Cargando suscripciones...</div>;
  }

  return (
    <div className="admin-push">
      <div className="admin-push-header">
        <h1>Gestion de Notificaciones Push</h1>
      </div>

      {error && <div className="error-message">{error}</div>}
      {successMessage && <div className="success-message">{successMessage}</div>}

      {stats && (
        <div className="push-stats-grid">
          <div className="push-stat-card">
            <span className="stat-icon">📲</span>
            <div className="stat-content">
              <span className="stat-value">{stats.total}</span>
              <span className="stat-label">Suscripciones totales</span>
            </div>
          </div>
          <div className="push-stat-card">
            <span className="stat-icon">👥</span>
            <div className="stat-content">
              <span className="stat-value">{stats.unique_users}</span>
              <span className="stat-label">Usuarios unicos</span>
            </div>
          </div>
          <div className="push-stat-card">
            <span className="stat-icon">📊</span>
            <div className="stat-content">
              <span className="stat-value">
                {stats.unique_users > 0 ? (stats.total / stats.unique_users).toFixed(1) : 0}
              </span>
              <span className="stat-label">Dispositivos por usuario</span>
            </div>
          </div>
        </div>
      )}

      {subscriptions.length === 0 ? (
        <div className="empty-state">
          <span className="empty-icon">🔕</span>
          <p>No hay suscripciones push activas</p>
        </div>
      ) : (
        <div className="subscriptions-table-container">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Usuario</th>
                <th>Navegador</th>
                <th>Fecha registro</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {subscriptions.map((sub) => {
                const browser = getBrowserFromEndpoint(sub.endpoint);
                return (
                  <tr key={sub.id}>
                    <td>
                      <div className="user-info">
                        <span className="user-name">{sub.user_name}</span>
                        <span className="user-email">{sub.user_email}</span>
                      </div>
                    </td>
                    <td>
                      <span className="browser-badge">
                        {browser.icon} {browser.name}
                      </span>
                    </td>
                    <td>{formatDate(sub.created_at)}</td>
                    <td>
                      <button
                        onClick={() => handleDeleteSubscription(sub.id)}
                        className="btn btn-delete"
                        title="Eliminar suscripcion"
                      >
                        🗑️
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AdminPushNotifications;
